
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import AuthLayout from "./AuthLayout";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: "https://tailor-admin.falconstaging.com/auth/reset-password",
      });

      if (error) {
        setError(error.message);
        toast.error("Password reset failed", { description: error.message });
      } else {
        setSubmitted(true);
        toast.success("Password reset email sent", {
          description: "Check your email for the reset link"
        });
      }
    } catch (err) {
      console.error("Unexpected error during password reset:", err);
      setError("An unexpected error occurred. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  if (submitted) {
    return (
      <AuthLayout 
        title="Check your email"
        subtitle="We've sent a password reset link to your email address"
      >
        <div className="mt-8 text-center">
          <p className="text-muted-foreground mb-4">
            If you don't see the email in your inbox, check your spam folder
          </p>
          <Button asChild className="mt-4">
            <Link to="/auth/sign-in">Return to sign in</Link>
          </Button>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Forgot password"
      subtitle="Enter your email to reset your password"
    >
      <form onSubmit={handleSubmit} className="mt-8 space-y-6">
        {error && (
          <div className="rounded-md bg-destructive/10 p-3">
            <div className="text-sm text-destructive">{error}</div>
          </div>
        )}

        <div>
          <Label htmlFor="email">Email address</Label>
          <Input
            id="email"
            name="email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="mt-1"
          />
        </div>

        <div>
          <Button
            type="submit"
            className="w-full"
            disabled={isLoading}
          >
            {isLoading ? "Sending reset link..." : "Send reset link"}
          </Button>
        </div>

        <div className="text-sm text-center">
          <p>
            Remember your password?{" "}
            <Link to="/auth/sign-in" className="font-medium text-primary hover:underline">
              Sign in
            </Link>
          </p>
        </div>
      </form>
    </AuthLayout>
  );
}
